import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/BrandingContext';
import { API_BASE_URL } from '../../lib/utils';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import ThemeToggle from '../../components/ThemeToggle';

const SuperAdminLoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const { branding } = useBranding();

  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/superadmin/auth/login', { email, password });
      const { accessToken, refreshToken, ...userData } = response.data;
      
      login(userData, accessToken);
      toast.success('Welcome back!');
      navigate('/superadmin');
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.detail || 'Login failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="fixed top-4 right-4 z-10">
        <ThemeToggle />
      </div>
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          {branding?.logo_url ? (
            <img
              src={`${API_BASE_URL}${branding.logo_url}?v=${encodeURIComponent(String(branding.rev || 0))}`}
              alt={branding?.brand_name || 'Logo'}
              className="mx-auto w-12 h-12 rounded object-contain mb-4"
            />
          ) : (
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary mb-4">
              <span className="text-white font-bold text-xl">W</span>
            </div>
          )}
          <h1 className="text-xl font-semibold text-foreground">{branding?.brand_name || 'WingsDiet'} SuperAdmin</h1>
          {branding?.slogan ? (
            <p className="text-sm text-muted-foreground mt-1">{branding.slogan}</p>
          ) : (
            <p className="text-sm text-muted-foreground mt-1">Sign in to your account</p>
          )}
        </div>

        {/* Login Form */}
        <div className="panel border border-border p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-card"
                placeholder="superadmin@wingsdiet.com"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-card"
                placeholder="Enter your password"
                data-testid="login-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full py-2.5 rounded-md text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="login-submit-btn"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          WingsDiet Admin Panel v1.0
        </p>
      </div>
    </div>
  );
};

export default SuperAdminLoginPage;
